// Render children into a module's `.module-sub-controls` strip — the fixed row
// between the header and the body that needsPlayer:false modules (League Table,
// Ranking, Filter, Squad) build for their own controls. In the legacy renderers
// this is a sibling of .module-body (not inside it), with its own padding / bg /
// bottom-border in styles.css, so a converted module renders it via a portal into a
// real sibling element rather than inside the body — keeping the CSS contract exact.

import { useState, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { watchControlsOverflow } from "../canvas/controls-overflow.js";

interface Props {
  mod: HTMLElement;
  body: HTMLElement;
  children: ReactNode;
}

export function SubControls({ mod, body, children }: Props) {
  const [el] = useState(() => {
    const d = document.createElement("div");
    d.className = "module-sub-controls";
    return d;
  });

  useEffect(() => {
    // Defensive single-strip guarantee: remove any other .module-sub-controls on this
    // module first (a stray/orphaned one from a prior renderer), so a type-switch can
    // never leave two bars stacked. Then insert ours immediately before the body (its
    // position in the legacy DOM).
    mod.querySelectorAll(":scope > .module-sub-controls").forEach((s) => {
      if (s !== el) s.remove();
    });
    mod.insertBefore(el, body);
    // Same overflow watcher the legacy renderers wire when they build the strip.
    watchControlsOverflow(mod);
    return () => {
      el.remove();
    };
  }, [mod, body, el]);

  return createPortal(children, el);
}
